import React from "react";
import { Box, Paper, Typography, useTheme } from "@mui/material";
import { InfoOutlined, ListAltOutlined } from "@mui/icons-material";
import { PieChart } from "@mui/x-charts/PieChart";
import { getChartColors } from "theme";
import { HtmlTooltip } from "components/HeatMap";
import { appendTooltipGlossary } from "components/reports/shared/tooltipGlossary";

/**
 * ReportPieChart - Wrapper for MUI PieChart with consistent styling
 *
 * @param {string} title - Chart title
 * @param {Array} data - Array of pie data: [{ id: 1, value: 100, label: 'Item 1' }]
 * @param {number} height - Chart height in pixels
 * @param {boolean} showInPaper - Wrap in Paper component
 * @param {boolean} showLabels - Show labels on slices
 * @param {boolean} showLegend - Show legend
 * @param {'right'|'bottom'} legendPosition - Legend placement
 * @param {Function} valueFormatter - Function to format values
 * @param {number} innerRadius - Inner radius for donut chart (0 for full pie)
 * @param {number} outerRadius - Outer radius
 * @param {Function} onSliceClick - Function called when slice is clicked, receives slice data
 * @param {string} tooltipTitle - Optional tooltip title for info icon
 * @param {string} tooltipContent - Optional tooltip content/explanation
 */
const ReportPieChart = ({
  title,
  data = [],
  height = 400,
  showInPaper = true,
  showLabels = false,
  showLegend = true,
  legendPosition = "right",
  valueFormatter = (value) => value?.toLocaleString(),
  //innerRadius = 10,
  //outerRadius = 100,
  innerRadius = 50,
  outerRadius = 100,
  arcLabelMinAngle = 20,
  paddingAngle = 2,
  cornerRadius = 10,
  onSliceClick,
  tooltipTitle,
  tooltipContent,
  tooltipLegend,
}) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const resolvedTooltipContent = appendTooltipGlossary(tooltipContent);

  // Use semantic color palette from theme
  const defaultColors = chartColors.series;

  // Add colors to data if not present
  const dataWithColors = data.map((item, index) => ({
    ...item,
    color: item.color || defaultColors[index % defaultColors.length],
  }));

  const chartContent = (
    <Box>
      {title && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2 }}>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          {resolvedTooltipContent && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto" }}>
                  {tooltipTitle && (
                    <Typography
                      variant="subtitle2"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {tooltipTitle}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                    {resolvedTooltipContent}
                  </Typography>
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <InfoOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.primary.main,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                }}
              />
            </HtmlTooltip>
          )}
          {tooltipLegend && tooltipLegend.length > 0 && (
            <HtmlTooltip
              title={
                <Box sx={{ p: 1, maxHeight: "400px", overflowY: "auto", minWidth: 220 }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Full Breakdown
                  </Typography>
                  {tooltipLegend.map((item, i) => (
                    <Box key={i} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "2px",
                          flexShrink: 0,
                          bgcolor: item.color || theme.palette.primary.main,
                        }}
                      />
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        {item.label}
                      </Typography>
                      {item.value != null && (
                        <Typography variant="body2" fontWeight="600" sx={{ whiteSpace: "nowrap" }}>
                          {item.value}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              }
              arrow
              placement="bottom-start"
            >
              <ListAltOutlined
                sx={{
                  fontSize: 18,
                  color: theme.palette.text.secondary,
                  cursor: "help",
                  opacity: 0.7,
                  "&:hover": { opacity: 1 },
                }}
              />
            </HtmlTooltip>
          )}
        </Box>
      )}
      {data.length === 0 ? (
        <Box
          sx={{
            height,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: theme.palette.background.alt,
            borderRadius: "8px",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No data available
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <PieChart
            series={[
              {
                data: dataWithColors,
                innerRadius,
                outerRadius,
                arcLabelMinAngle,
                paddingAngle,
                cornerRadius,
                highlightScope: { faded: "global", highlighted: "item" },
                valueFormatter: (item) => valueFormatter(item.value),
                arcLabel: showLabels ? (item) => `${item.value}` : undefined,
              },
            ]}
            height={height}
            margin={{
              top: 10,
              right: legendPosition === "right" ? 140 : 10,
              left: 10,
              bottom: legendPosition === "bottom" ? 70 : 20,
            }}
            onItemClick={
              onSliceClick
                ? (event, d) => {
                    const item = data[d.dataIndex];
                    if (item) onSliceClick(item);
                  }
                : undefined
            }
            slotProps={{
              legend: showLegend
                ? {
                    direction: legendPosition === "bottom" ? "row" : "column",
                    position:
                      legendPosition === "bottom"
                        ? { vertical: "bottom", horizontal: "middle" }
                        : { vertical: "middle", horizontal: "right" },
                    padding: 0,
                    itemMarkWidth: 12,
                    itemMarkHeight: 12,
                    markGap: 8,
                    itemGap: 8,
                  }
                : { hidden: true },
            }}
            sx={{
              "& .MuiChartsLegend-label": {
                fill: theme.palette.text.primary,
                fontSize: "0.875rem",
              },
              "& .MuiChartsLegend-mark": {
                rx: 2,
                ry: 2,
              },
              "& .MuiChartsLegend-series rect": {
                rx: 2,
                ry: 2,
              },
              ...(onSliceClick && {
                "& .MuiPieArc-root": {
                  cursor: "pointer",
                },
              }),
            }}
          />
        </Box>
      )}
    </Box>
  );

  if (showInPaper) {
    return (
      <Paper
        sx={{
          p: 3,
          bgcolor: theme.palette.background.alt,
          border: `1px solid ${theme.palette.primary.border2}`,
          borderRadius: "12px",
        }}
      >
        {chartContent}
      </Paper>
    );
  }

  return chartContent;
};

export default ReportPieChart;
